import React, { useState, useEffect } from 'react';
import { Button, Space, Modal, Form, Input, Select, Card } from 'antd';
import {
  PlusOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import type {
  ManagerDetailResponse,
  ManagerAddDto,
  ManagerUpdateDto,
  ManagerRoleBaseResData,
} from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
} from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

// 使用API返回的数据类型
type UserData = ManagerDetailResponse;

const UserManagement: React.FC = () => {
  // 角色列表状态
  const [roles, setRoles] = useState<ManagerRoleBaseResData[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // 获取角色列表
  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await Service.managerRoleControllerGet(1, 1000); // 获取所有角色
      if (response && response.data && response.data.rows) {
        setRoles(response.data.rows);
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  // 组件挂载时获取角色列表
  useEffect(() => {
    fetchRoles();
  }, []);

  // 使用带搜索功能的表格管理Hook
  const {
    data: users,
    loading,
    pagination,
    fetchData: fetchUsers,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<UserData>({
    fetchData: (pageNum, pageSize, custom) =>
      Service.managerControllerGet(pageNum, pageSize, custom),
    deleteData: async (id: number) => {
      await Service.managerControllerDelete(id);
    },
    errorMessage: '获取用户列表失败',
    searchOptions: {
      fields: ['managerName', 'realName', 'phone', 'managerUid'],
    },
  });

  // 使用Modal管理Hook
  const {
    visible,
    editingItem: editingUser,
    form,
    handleAdd,
    handleEdit: originalHandleEdit,
    handleOk,
    handleCancel,
  } = useFormModal<UserData>({
    createData: async (data: ManagerAddDto) => {
      // 处理多选角色数据
      if (data.role && Array.isArray(data.role)) {
        data.role = data.role.join(',');
      }
      const res = await Service.managerControllerAdd(data) as { errNo?: number; errStr?: string };
      if (res?.errNo !== undefined && res.errNo !== 0) {
        throw new Error(res.errStr || '操作失败');
      }
    },
    updateData: async (data: ManagerUpdateDto) => {
      // 处理多选角色数据
      if (data.role && Array.isArray(data.role)) {
        data.role = data.role.join(',');
      }
      const res = await Service.managerControllerPut(data) as { errNo?: number; errStr?: string };
      if (res?.errNo !== undefined && res.errNo !== 0) {
        throw new Error(res.errStr || '操作失败');
      }
    },
  });

  // 自定义编辑处理函数，支持多角色（使用 roleKey）
  const handleEdit = (record: UserData) => {
    const roleKeys = record.role
      ? record.role.split(',').filter((k: string) => k.trim())
      : [];
    const formData = {
      ...record,
      role: roleKeys.map((k: string) => String(k.trim())),
    };
    originalHandleEdit(record);
    setTimeout(() => {
      form.setFieldsValue(formData);
    }, 0);
  };

  // 处理Modal确认后的数据刷新
  const handleModalOkWithRefresh = async () => {
    const success = await handleOk();
    if (success) {
      fetchUsers();
    }
  };

  const columns: ColumnsType<UserData> = [
    createLongTextColumn('用户名', 'managerName', 8),
    createLongTextColumn('真实姓名', 'realName', 8),
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    createLongTextColumn('UID', 'managerUid', 12),
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (roleValue: string) => {
        if (!roleValue) return '-';

        const keys = roleValue.split(',').filter((k: string) => k.trim());
        if (keys.length === 0) return '-';

        if (keys.length === 1) {
          const role = roles.find(r => r.roleKey === keys[0]);
          const roleName = role ? role.roleName : keys[0];
          const style = {
            color: roleName === '超级管理员' ? '#cf1322' : '#1890ff',
            backgroundColor: roleName === '超级管理员' ? '#fff2f0' : '#f0f5ff',
            border: `1px solid ${roleName === '超级管理员' ? '#ffccc7' : '#d6e4ff'}`,
            borderRadius: '4px',
            padding: '2px 8px',
            fontSize: '12px',
            display: 'inline-block',
          };
          return <span style={style}>{roleName}</span>;
        }

        const roleNames = keys.map((key: string) => {
          const role = roles.find(r => r.roleKey === key);
          return role ? role.roleName : key;
        });
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {roleNames.map((name, index) => (
              <span
                key={index}
                style={{
                  color: name === '超级管理员' ? '#cf1322' : '#1890ff',
                  backgroundColor: name === '超级管理员' ? '#fff2f0' : '#f0f5ff',
                  border: `1px solid ${name === '超级管理员' ? '#ffccc7' : '#d6e4ff'}`,
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  display: 'inline-block',
                }}
              >
                {name}
              </span>
            ))}
          </div>
        );
      },
    },
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  // 统计卡片数据
  const stats: DataStat[] = [
    {
      title: '总用户数',
      value: pagination.total,
      prefix: <UserOutlined />,
      valueStyle: { color: '#3f8600' },
    },
    {
      title: '当前页用户',
      value: users.length,
      valueStyle: { color: '#1890ff' },
    },
    {
      title: '管理员',
      value: users.filter(u => {
        if (!u.role) return false;
        const roleKeys = u.role.split(',').filter((k: string) => k.trim());
        return roleKeys.some(key => {
          const role = roles.find(r => r.roleKey === key);
          return role && role.roleName === '超级管理员';
        });
      }).length,
      valueStyle: { color: '#cf1322' },
    },
    {
      title: '普通用户',
      value: users.filter(u => {
        if (!u.role) return false;
        const roleKeys = u.role.split(',').filter((k: string) => k.trim());
        return roleKeys.some(key => {
          const role = roles.find(r => r.roleKey === key);
          return role && role.roleName === '普通用户';
        });
      }).length,
      valueStyle: { color: '#722ed1' },
    },
  ];

  // 操作按钮配置
  const actionButtons: ActionButton<UserData>[] = [
    {
      key: 'edit',
      label: '编辑',
      icon: <EditOutlined />,
      onClick: handleEdit,
    },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: record => handleDelete(record.managerId),
      confirm: {
        title: '确定要删除这个用户吗？',
        okText: '确定',
        cancelText: '取消',
      },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        {/* 统计卡片 */}
        <DataStats stats={stats} className={styles.statsRow} />

        {/* 操作栏 */}
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
              新增用户
            </Button>

            {/* 搜索区域 */}
            <SearchBar
              search={search}
              placeholder='搜索用户名、真实姓名、手机号或UID'
              width={300}
              loading={loading}
            />
          </Space>
        </div>

        {/* 用户表格 */}
        <DataTable
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey='managerId'
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>

      {/* 新增/编辑用户弹窗 */}
      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={visible}
        onOk={handleModalOkWithRefresh}
        onCancel={handleCancel}
        width={600}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='managerName'
            label='用户名'
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder='请输入用户名' />
          </Form.Item>
          <Form.Item
            name='realName'
            label='真实姓名'
            rules={[{ required: true, message: '请输入真实姓名' }]}
          >
            <Input placeholder='请输入真实姓名' />
          </Form.Item>
          <Form.Item
            name='phone'
            label='手机号'
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
            ]}
          >
            <Input placeholder='请输入手机号' />
          </Form.Item>
          <Form.Item
            name='password'
            label='密码'
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password placeholder='请输入密码' />
          </Form.Item>
          <Form.Item
            name='role'
            label='角色'
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select
              mode='multiple'
              placeholder='请选择角色'
              loading={rolesLoading}
              showSearch
              optionFilterProp='children'
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              maxTagCount='responsive'
            >
              {roles.map(role => (
                <Select.Option key={role.roleId} value={role.roleKey}>
                  {role.roleName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
